const CategoryItemList = require('@/lib/Items/CategoryItemList');
const CategoryItemListCollection = require('@/lib/Items/CategoryItemListCollection');
// Because we want to spyOn on of these functions we can't destructure here.
// So instead of const { compareStringsForFilter } = require('@/functions/strings');
// We need to use 'strings' and access the functions as strings.something() rather than just something().
const strings = require('@/functions/strings');

/**
 * @typedef {import('@/lib/Items/AbstractItem')} AbstractItem
 * @typedef {import('@/lib/Sessions/SessionEvent')} SessionEvent
 */

/**
 * @param {AbstractItem[]} items
 *
 * @return {{eventButtons: {navOrder: number, event: SessionEvent, items: AbstractItem[]}[], nonEventItems:
 *     AbstractItem[]}}
 */
const separateEventButtonsFromItems = (items) => {
    let eventButtons = {};
    let nonEventItems = [];

    items.forEach((item) => {
        if (item.event) {
            if (!eventButtons[item.event.ID]) {
                eventButtons[item.event.ID] = {
                    navOrder: item.event.navOrder,
                    event: item.event,
                    items: [],
                };
            }

            eventButtons[item.event.ID].items.push(item);
            return;
        }

        nonEventItems.push(item);
    });

    return {
        eventButtons: Object.values(eventButtons),
        nonEventItems,
    };
};

/**
 * @param {AbstractItem[]} items
 * @param {{string: string, words: string[]}} normalizedSearchQuery
 *
 * @return {AbstractItem[]}
 */
const filterItemsForSearch = (items, normalizedSearchQuery) => {
    // Fix for bug "match all" entries
    if (!normalizedSearchQuery?.string && !normalizedSearchQuery?.words?.length) {
        return [];
    }

    // EDGE CASE symbol-only searches (e.g. "==\" or "&") would return no results.
    // If the search is only symbols, search as-is.
    if (normalizedSearchQuery.string && !normalizedSearchQuery.words?.length) {
        normalizedSearchQuery.words = [normalizedSearchQuery.string];
    }

    return items.filter((item) => {
        // Search by item name, short name, code and barcodes
        const { name, shortName, code, barcodes } = item;

        // Set the preferred search order by building an array of values.
        // Note that items can now have multiple barcodes.
        // e.g.
        // [
        //     'Hot Dog',
        //     'Dawg',
        //     'HOT_DOG',
        //     '0118999',
        //     '8819991',
        // ]
        const searchValues = [name, shortName, code, ...(barcodes ?? [])];

        // Apply partial search for everything until match
        return searchValues.some(
            (fieldValue) => {
                return fieldValue && strings.compareStringsForFilter(fieldValue, normalizedSearchQuery);
            }
        );
    });
};

/**
 * @param {{navOrder: number, event: SessionEvent, items: AbstractItem[]}[]} eventButtons
 * @param {{string: string, words: string[]}} normalizedSearchQuery
 *
 * @return {{navOrder: number, event: SessionEvent, items: AbstractItem[]}[]}
 */
const filterEventButtonsForSearch = (eventButtons, normalizedSearchQuery) => {
    // We want to show on each event button any ticket names inside the event that matched the search.
    // To do this we'll first build the list of matching tickets.
    eventButtons.forEach((evt) => {
        evt.matchingItems = filterItemsForSearch(evt.items, normalizedSearchQuery);
    });

    // If a search query has been entered, filter the event buttons to only
    // those whose event names matches, or contain a ticket whose name matches.
    return eventButtons.filter((evt) => {
        // Show if it has any matching item names.
        if (evt.matchingItems.length > 0) {
            return true;
        }

        // Show if the event name matches.
        if (strings.compareStringsForFilter(evt.event.name, normalizedSearchQuery)) {
            return true;
        }

        return false;
    });
};

/**
 * This receives a CategoryItemListCollection and returns a new CategoryItemListCollection
 * which contains only items/events that match the given search query.
 *
 * @param {CategoryItemListCollection} collections
 * @param {{string: string, words: string[]}} normalizedSearchQuery
 *
 * @return {CategoryItemListCollection}
 */
const searchCategoryItemListCollection = (collections, normalizedSearchQuery) => {
    // Build the search results object.
    const searchResults = new CategoryItemListCollection();

    Object.values(collections.categories).forEach(
        /**
         * @param {CategoryItemList} collection
         */
        (collection) => {
            let eventButtonResults = filterEventButtonsForSearch(
                collection.eventButtons,
                normalizedSearchQuery,
            );
            let itemResults = filterItemsForSearch(
                collection.items,
                normalizedSearchQuery,
            );

            if (eventButtonResults.length < 1 && itemResults.length < 1) {
                // There were no results in this category - skip it.
                return;
            }

            searchResults.add(
                new CategoryItemList(collection.category, eventButtonResults, itemResults),
            );
        },
    );

    return searchResults;
};

/**
 * Returns visible event buttons (and their tickets) for the usage in CategoryItemList.
 *
 * @param {{navOrder: number, event: SessionEvent, items: AbstractItem[]}[]} eventButtons
 * @param {number[]} visibleEventIDs
 * @param {number[]} visibleItemIDs
 *
 * @return {{navOrder: number, event: SessionEvent, items: AbstractItem[]}[]}
 */
const getVisibleEventButtons = (eventButtons, visibleEventIDs = [], visibleItemIDs = []) => {
    // If there are no visible events and items then return empty array.
    // This is also means that there are only visible categories selected.
    if (!visibleEventIDs.length && !visibleItemIDs.length) {
        return [];
    }
    /**
     * @type {{navOrder: number, event: SessionEvent, items: AbstractItem[]}[]}
     */
    const filteredEventButtons = [];
    eventButtons.forEach((eventButton) => {
        // Check if event itself is visible. It should include all tickets if so.
        if (visibleEventIDs.length && visibleEventIDs.includes(eventButton.event.ID)) {
            filteredEventButtons.push(eventButton);
            return;
        }
        // Check event items visiblity.
        if (visibleItemIDs.length) {
            const matchingItems = eventButton.items.filter((item) => visibleItemIDs.includes(item.ID));
            if (matchingItems.length) {
                filteredEventButtons.push({ ...eventButton, items: matchingItems });
            }
        }
    });
    return filteredEventButtons;
};

/**
 * This receives collections of CategoryItemListCollection and returns the similar object
 * which contains only visible categories/items/events.
 *
 * @param {Object<string, CategoryItemListCollection>} collections
 * @param {{catIDs: number[], eventIDs: number[], itemIDs: number[]}} visibleLists
 *
 * @return {Object<string, CategoryItemListCollection>}
 */
const getVisibleItemListCollectionsByItemType = (collections, visibleLists) => {
    const {
        catIDs = [],
        eventIDs = [],
        itemIDs = [],
    } = visibleLists || {};

    // Additional condition just in case. This method shouldn't be called if all visible lists are empty.
    if (!catIDs.length && !eventIDs.length && !itemIDs.length) {
        return collections;
    }

    /**
     * All of the saleable items organised by item type.
     * Everything is additionally filtered by the visible lists of categories/events/items.
     * There is one CategoryItemListCollection for each item type.
     *
     * @type {Object<string, CategoryItemListCollection>}
     */
    const result = {};
    Object.entries(collections).forEach(([itemType, collection]) => {
        const visibleCategoryItemListCollection = new CategoryItemListCollection();
        Object.values(collection.categories).forEach(
            /**
             * @param {CategoryItemList} cat
             */
            (cat) => {
                // The category is visible so all the items in the category are also visible.
                if (catIDs.length && catIDs.includes(cat.category.ID)) {
                    visibleCategoryItemListCollection.add(cat);
                    return;
                }

                // Now check if there are any visible items or events. A category needs to be visible if it contains
                // visible items or events although only these items or events will be visible.

                // First find visible events.
                const eventButtons = getVisibleEventButtons(
                    cat.eventButtons,
                    eventIDs,
                    itemIDs,
                );

                // Next find visible items.
                const items = itemIDs.length
                    ? cat.items.filter((item) => itemIDs.includes(item.ID))
                    : [];

                // If there are no visible items or events ignore the category.
                if (!items.length && !eventButtons.length) {
                    return;
                }
                visibleCategoryItemListCollection.add(
                    new CategoryItemList(cat.category, eventButtons, items),
                );
            }
        );

        // Ignore empty collections.
        if (Object.values(visibleCategoryItemListCollection.categories).length) {
            result[itemType] = visibleCategoryItemListCollection;
        }
    });
    return result;
};

module.exports = {
    filterItemsForSearch,
    getVisibleEventButtons,
    getVisibleItemListCollectionsByItemType,
    searchCategoryItemListCollection,
    separateEventButtonsFromItems,
};
